import React, { useEffect, useId, useState } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import { Calendar, CalendarPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import { supabase } from '../../supabaseClient';
import { captureConnectionsEvent } from '../../lib/captureConnectionsEvent';
import {
  daysUntilExam,
  getExamCountdown,
  toLocalDateString,
} from '../../lib/examDate';
import { useIsSmartphone } from '../../hooks/useIsSmartphone';

const canHover = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: hover)').matches;

const toneClasses = {
  calm: {
    chip: 'bg-butter-200 text-neutral-900 font-semibold',
    dot: 'bg-butter-700',
  },
  final: {
    chip: 'bg-butter-500 text-neutral-900 font-bold',
    dot: 'bg-neutral-900',
  },
  passed: {
    chip: 'bg-neutral-100 text-neutral-600 font-semibold',
    dot: 'bg-neutral-400',
  },
};

export const ExamChip = ({ label, tone = 'calm', className = '' }) => {
  const tones = toneClasses[tone] || toneClasses.calm;
  return (
    <span
      className={`inline-flex items-center justify-center h-7 w-7 rounded-full text-[13px] whitespace-nowrap shrink-0 sm:w-auto sm:gap-[7px] sm:px-2.5 ${tones.chip} ${className}`}
    >
      <span
        className={`block h-[7px] w-[7px] rounded-full shrink-0 ${tones.dot}`}
        aria-hidden="true"
      />
      <span className="hidden sm:inline">{label}</span>
    </span>
  );
};

export const ExamTooltip = ({
  id,
  text,
  children,
  disabled = false,
}) => {
  const showOnHoverCapable = canHover() && !disabled && Boolean(text);

  return (
    <div className="relative group">
      {children}
      {showOnHoverCapable && (
        <div
          id={id}
          role="tooltip"
          className="absolute left-0 top-full mt-2 z-50 whitespace-nowrap rounded-lg bg-neutral-900 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100"
        >
          {text}
        </div>
      )}
    </div>
  );
};

export const ExamDateField = ({ value, onChange }) => {
  const today = toLocalDateString(new Date());
  const countdown = value ? getExamCountdown(value) : null;

  const ariaLabel = countdown
    ? `Exam date: ${countdown.label}. Change exam date`
    : 'Pick an exam date';

  const handleChange = (event) => {
    onChange?.(event.target.value || '');
  };

  return (
    <div className="relative">
      <div
        className="w-full flex items-center gap-2.5 px-3.5 py-3 rounded-xl border border-neutral-200 bg-butter-50 text-left pointer-events-none"
        aria-hidden="true"
      >
        {countdown ? (
          <>
            <span
              className={`block h-[7px] w-[7px] rounded-full shrink-0 ${
                toneClasses[countdown.tone]?.dot || toneClasses.calm.dot
              }`}
            />
            <span className="flex-1 font-semibold text-neutral-900">
              {countdown.label}
            </span>
            <span className="text-sm font-medium text-neutral-600">Change</span>
          </>
        ) : (
          <>
            <Calendar size={18} className="shrink-0 text-neutral-500" aria-hidden="true" />
            <span className="flex-1 font-medium text-neutral-500">Pick a date</span>
          </>
        )}
      </div>
      <input
        type="date"
        min={today}
        value={value || ''}
        onChange={handleChange}
        onClick={(e) => {
          try {
            e.currentTarget.showPicker();
          } catch (_) {
            /* overlay still works on platforms without showPicker */
          }
        }}
        aria-label={ariaLabel}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
  );
};

const ExamCountdownPanel = ({
  draft,
  onDraftChange,
  savedDate,
  onSave,
  onRemove,
  isSaving,
  isSmartphone,
}) => (
  <div
    className={
      isSmartphone
        ? 'w-full bg-white rounded-t-xl border border-neutral-200 shadow-lg p-4 space-y-3'
        : 'w-[300px] bg-white rounded-xl border border-neutral-200 shadow-lg p-4 space-y-3'
    }
  >
    <p className="text-sm font-semibold text-neutral-900">Exam date</p>
    <ExamDateField value={draft} onChange={onDraftChange} />
    <div className="border-t border-neutral-200 pt-3 flex items-center justify-between gap-3">
      {savedDate ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={isSaving}
          className="text-sm font-medium text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
        >
          Remove date
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving || !draft}
        className={`px-5 py-2.5 bg-cranberry-700 text-white rounded-xl border-2 border-neutral-900 font-medium ${
          isSaving || !draft
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-cranberry-600 transition-colors'
        }`}
      >
        {isSaving ? 'Saving...' : 'Save'}
      </button>
    </div>
  </div>
);

/**
 * Workspace exam countdown chip + edit popover for the Connections header.
 */
const ExamCountdown = ({ workspaceId }) => {
  const isSmartphone = useIsSmartphone();
  const tooltipId = useId();
  const [examDate, setExamDate] = useState(null);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!workspaceId) {
        if (!cancelled) {
          setExamDate(null);
          setDraft('');
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      const { data, error } = await supabase
        .from('workspaces')
        .select('exam_date')
        .eq('id', workspaceId)
        .single();

      if (cancelled) return;

      if (error) {
        console.error('Error loading exam date:', error);
        setExamDate(null);
        setDraft('');
      } else {
        const next = data?.exam_date || null;
        setExamDate(next);
        setDraft(next || '');
      }
      setIsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const countdown = examDate ? getExamCountdown(examDate) : null;
  const days = examDate ? daysUntilExam(examDate) : null;

  const triggerAriaLabel =
    examDate && days !== null
      ? days < 0
        ? 'Exam date has passed. Change exam date'
        : days === 0
          ? 'Exam day. Change exam date'
          : `Exam in ${days} days. Change exam date`
      : 'Pick an exam date';

  const persistExamDate = async (nextDate, close) => {
    if (!workspaceId || isSaving) return;
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('workspaces')
        .update({
          exam_date: nextDate,
          updated_at: new Date().toISOString(),
        })
        .eq('id', workspaceId);

      if (error) throw error;

      setExamDate(nextDate);
      setDraft(nextDate || '');

      if (nextDate) {
        captureConnectionsEvent('exam_date_set', {
          source: 'edit',
          days_until: daysUntilExam(nextDate),
        });
      } else {
        captureConnectionsEvent('exam_date_removed');
      }

      close?.();
    } catch (error) {
      console.error('Error saving exam date:', error);
      toast.error('Could not save exam date. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !workspaceId) return null;

  return (
    <Popover className="relative shrink-0">
      {({ open, close }) => (
        <>
          {countdown ? (
            <ExamTooltip
              id={tooltipId}
              text={countdown.tooltip}
              disabled={open}
            >
              <PopoverButton
                className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700 focus-visible:ring-offset-1"
                aria-label={triggerAriaLabel}
                aria-describedby={!open ? tooltipId : undefined}
                onClick={() => setDraft(examDate || '')}
              >
                <ExamChip label={countdown.label} tone={countdown.tone} />
              </PopoverButton>
            </ExamTooltip>
          ) : (
            <PopoverButton
              className="inline-flex items-center justify-center rounded-full w-9 h-9 text-neutral-500 hover:bg-neutral-100 sm:w-auto sm:h-auto sm:rounded sm:hover:bg-transparent sm:text-[13px] sm:font-medium sm:text-neutral-600 sm:hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-cranberry-700"
              aria-label="Add exam date"
              onClick={() => setDraft(examDate || '')}
            >
              <CalendarPlus size={20} aria-hidden="true" className="sm:hidden" />
              <span className="hidden sm:inline" aria-hidden="true">
                + Add exam date
              </span>
            </PopoverButton>
          )}

          {open && isSmartphone && (
            <div
              className="fixed inset-0 z-40 bg-black/40"
              aria-hidden="true"
              onClick={() => close()}
            />
          )}

          <PopoverPanel
            {...(isSmartphone
              ? {
                  className:
                    'fixed inset-x-0 bottom-0 z-50 focus:outline-none',
                }
              : {
                  anchor: { to: 'bottom start', gap: '8px' },
                  className: 'z-50 focus:outline-none',
                })}
          >
            <ExamCountdownPanel
              draft={draft}
              onDraftChange={setDraft}
              savedDate={examDate}
              isSaving={isSaving}
              isSmartphone={isSmartphone}
              onSave={() => persistExamDate(draft || null, close)}
              onRemove={() => persistExamDate(null, close)}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

export default ExamCountdown;
